import { useDashboard } from '@/hooks/useApi';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { BellIcon } from '@heroicons/react/24/outline';

export default function ActivityFeed() {
  const { t } = useTranslation();
  const { data, isLoading } = useDashboard();

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-12 rounded-lg bg-gray-50 animate-pulse" />
        ))}
      </div>
    );
  }

  const overdueInvoices = data?.overdueInvoices || [];
  const topClients = data?.topClients || [];
  const hasActivity = overdueInvoices.length > 0 || topClients.length > 0;

  if (!hasActivity) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 mb-3">
          <BellIcon className="h-6 w-6 text-indigo-500" />
        </div>
        <p className="text-sm font-medium text-gray-900">{t('dashboard.emptyActivity')}</p>
        <p className="text-xs text-gray-500 mt-1">{t('dashboard.emptyActivityHint')}</p>
      </div>
    );
  }

  return (
    <ul className="space-y-3 max-h-72 overflow-y-auto pr-1" role="list">
      {overdueInvoices.map((inv) => (
        <li
          key={inv.id}
          className={cn(
            'flex items-start gap-3 rounded-lg p-2.5',
            'transition-all duration-200 ease-in-out hover:bg-gray-50',
          )}
        >
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[10px] font-bold uppercase bg-red-50 text-red-600">
            !
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">{t('dashboard.overdueInvoice')}</p>
            <p className="text-xs text-gray-500 truncate">{inv.invoiceNumber} — {inv.clientName} — €{parseFloat(inv.totalAmount).toLocaleString()}</p>
          </div>
          <time className="shrink-0 text-[11px] text-gray-400 mt-0.5">
            {new Date(inv.dueDate).toLocaleDateString()}
          </time>
        </li>
      ))}
      {topClients.map((client, i) => (
        <li
          key={i}
          className={cn(
            'flex items-start gap-3 rounded-lg p-2.5',
            'transition-all duration-200 ease-in-out hover:bg-gray-50',
          )}
        >
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[10px] font-bold uppercase bg-emerald-50 text-emerald-600">
            C
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">{client.clientName}</p>
            <p className="text-xs text-gray-500 truncate">€{parseFloat(client.revenue).toLocaleString()}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}
